import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface TextEditorStyle {
  fontWeight?: 'bold' | null;
  fontStyle?: 'italic' | null;
  textDecoration?: 'underline' | null;
  textAlign?: 'left' | 'center' | 'right' | 'justify' | null;
}

/**
 * TextEditorComponent - a compact rich-text input with a formatting toolbar for alignment, bold, italic and underline, exposing two-way bindings for text value and style.
 * @component
 * @selector app-text-editor
 * @example
 *   <app-text-editor
 *     [value]="sampleText"
 *     [placeholder]="'Type here...'"
 *     [style]="currentStyle"
 *     (valueChange)="onValueChange($event)"
 *     (styleChange)="onStyleChange($event)">
 *   </app-text-editor>
 *
 * Purpose:
 *   Provides a textarea with an attached toolbar that allows users to toggle font weight (bold), font style (italic),
 *   text decoration (underline) and text alignment (left/center/right/justify). The component emits changes to the
 *   text content and visual style so parent components can react or persist state.
 *
 * Inputs:
 * @input {string} value - The current text content of the editor. Defaults to an empty string. Emitted via valueChange on user input.
 * @input {string} placeholder - Placeholder text shown when the editor is empty. Defaults to 'Enter text...'.
 * @input {TextEditorStyle} style - Object describing visual styles (fontWeight, fontStyle, textDecoration, textAlign). Mutated by toolbar controls and emitted via styleChange.
 *
 * Outputs:
 * @output {EventEmitter<string>} valueChange - Emits the updated text value whenever the textarea input changes.
 * @output {EventEmitter<TextEditorStyle>} styleChange - Emits a copy of the current style object whenever a formatting control changes.
 *
 * Key methods:
 *   - onInput(value: string): Emits valueChange with the new text value when the user types.
 *   - toggleBold(): Toggles fontWeight between 'bold' and null, then emits styleChange.
 *   - toggleItalic(): Toggles fontStyle between 'italic' and null, then emits styleChange.
 *   - toggleUnderline(): Toggles textDecoration between 'underline' and null, then emits styleChange.
 *   - toggleAlignmentDropdown(): Opens or closes the alignment dropdown menu.
 *   - setAlignment(align: 'left'|'center'|'right'|'justify'): Sets or clears textAlign, closes the dropdown, and emits styleChange.
 *   - getAlignmentIcon(): Returns the path to the center alignment SVG used when no specific alignment icon is available.
 *   - getAlignmentIconClass(): Returns a CSS class corresponding to the current alignment for icon rendering.
 *   - emitStyleChange(): Internal helper that emits a shallow copy of the style via styleChange.
 *
 * Lifecycle hooks:
 *   - ngOnInit(): Attaches a document click listener to detect clicks outside the dropdown and close it.
 *   - ngOnDestroy(): Removes the document click listener to avoid memory leaks.
 */
@Component({
  selector: 'app-text-editor',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="text-editor">
      <div class="editor-toolbar">
        <!-- Left Group: Alignment with Dropdown -->
        <div class="toolbar-group left-group">
          <div class="dropdown-container">
            <button
              class="toolbar-btn alignment-btn"
              [class.active]="style.textAlign"
              (click)="toggleAlignmentDropdown()"
            >
              <div
                class="alignment-button-icon"
                *ngIf="style.textAlign !== 'center' && style.textAlign"
              >
                <div class="alignment-icon" [ngClass]="getAlignmentIconClass()"></div>
              </div>
              <img
                *ngIf="!style.textAlign || style.textAlign === 'center'"
                [src]="getAlignmentIcon()"
                alt="Alignment"
                width="14"
                height="11"
              />
              <img
                src="assets/images/website-builder/chevron-down-icon.svg"
                alt="Dropdown"
                width="8"
                height="5"
                class="dropdown-arrow"
                [class.rotated]="showAlignmentDropdown"
              />
            </button>
            <div class="dropdown-menu alignment-dropdown" *ngIf="showAlignmentDropdown">
              <button
                class="dropdown-item"
                [class.active]="style.textAlign === 'left'"
                (click)="setAlignment('left')"
              >
                <div class="alignment-icon left-align"></div>
                <span>Left</span>
              </button>
              <button
                class="dropdown-item"
                [class.active]="style.textAlign === 'center'"
                (click)="setAlignment('center')"
              >
                <img
                  src="assets/images/website-builder/align-center-icon.svg"
                  alt="Center"
                  width="14"
                  height="11"
                />
                <span>Center</span>
              </button>
              <button
                class="dropdown-item"
                [class.active]="style.textAlign === 'right'"
                (click)="setAlignment('right')"
              >
                <div class="alignment-icon right-align"></div>
                <span>Right</span>
              </button>
              <button
                class="dropdown-item"
                [class.active]="style.textAlign === 'justify'"
                (click)="setAlignment('justify')"
              >
                <div class="alignment-icon justify-align"></div>
                <span>Justify</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Center Group: AI/Sparkles Button -->
        <div class="toolbar-group center-group">
          <button class="toolbar-btn ai-btn">
            <img
              src="assets/images/website-builder/sparkles-icon.svg"
              alt="AI"
              width="14"
              height="14"
            />
          </button>
        </div>

        <!-- Right Group: Text Formatting Controls -->
        <div class="toolbar-group right-group">
          <button
            class="toolbar-btn"
            [class.active]="style.fontWeight === 'bold'"
            (click)="toggleBold()"
          >
            <img
              src="assets/images/website-builder/bold-icon.svg"
              alt="Bold"
              width="10"
              height="12"
            />
          </button>
          <button
            class="toolbar-btn"
            [class.active]="style.fontStyle === 'italic'"
            (click)="toggleItalic()"
          >
            <img
              src="assets/images/website-builder/italic-icon.svg"
              alt="Italic"
              width="9"
              height="12"
            />
          </button>
          <button
            class="toolbar-btn"
            [class.active]="style.textDecoration === 'underline'"
            (click)="toggleUnderline()"
          >
            <img
              src="assets/images/website-builder/underline-icon.svg"
              alt="Underline"
              width="12"
              height="12"
            />
          </button>
        </div>
      </div>
      <div class="editor-input">
        <textarea
          class="text-input"
          [value]="value"
          (input)="onInput($any($event.target).value)"
          [placeholder]="placeholder"
          [style.font-weight]="style.fontWeight || 'normal'"
          [style.font-style]="style.fontStyle || 'normal'"
          [style.text-decoration]="style.textDecoration || 'none'"
          [style.text-align]="style.textAlign || 'left'"
        >
        </textarea>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./text-editor.component.scss'],
})
export class TextEditorComponent implements OnInit, OnDestroy {
  @Input() value = '';
  @Input() placeholder = 'Enter text...';
  @Input() style: TextEditorStyle = {};
  @Output() valueChange = new EventEmitter<string>();
  @Output() styleChange = new EventEmitter<TextEditorStyle>();

  showAlignmentDropdown = false;

  onInput(value: string): void {
    this.valueChange.emit(value);
  }

  toggleBold(): void {
    this.style.fontWeight = this.style.fontWeight === 'bold' ? null : 'bold';
    this.emitStyleChange();
  }

  toggleItalic(): void {
    this.style.fontStyle = this.style.fontStyle === 'italic' ? null : 'italic';
    this.emitStyleChange();
  }

  toggleUnderline(): void {
    this.style.textDecoration = this.style.textDecoration === 'underline' ? null : 'underline';
    this.emitStyleChange();
  }

  toggleAlignmentDropdown(): void {
    this.showAlignmentDropdown = !this.showAlignmentDropdown;
  }

  setAlignment(align: 'left' | 'center' | 'right' | 'justify'): void {
    this.style.textAlign = this.style.textAlign === align ? null : align;
    this.showAlignmentDropdown = false;
    this.emitStyleChange();
  }

  getAlignmentIcon(): string {
    // Return center icon (the only one we have as SVG)
    return 'assets/images/website-builder/align-center-icon.svg';
  }

  getAlignmentIconClass(): string {
    const align = this.style.textAlign || 'left';
    if (align === 'left') return 'left-align';
    if (align === 'right') return 'right-align';
    if (align === 'justify') return 'justify-align';
    return 'left-align';
  }

  private emitStyleChange(): void {
    this.styleChange.emit({ ...this.style });
  }

  // Close dropdowns when clicking outside
  ngOnInit(): void {
    document.addEventListener('click', this.handleClickOutside.bind(this));
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.handleClickOutside.bind(this));
  }

  private handleClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown-container')) {
      this.showAlignmentDropdown = false;
    }
  }
}
